import {
  loginAll as sdkBridgeLoginAll,
  configure as configureSdkBridge,
  PROJECT_GOVERNANCE
} from '../governanceBridge.js'


// Mirrors shared/pages/auth/signin.js — fan out to Supabase federation
// after Symbols SDK login. Best-effort, non-blocking.
const _fanOutBridgeLogin = async (email, password) => {
  // Workspace login is the governance entry point — governance MUST mint
  // its session at login time, otherwise every /workspace/* call 401s.
  // Re-assert the workspace-project app config at the call site because
  // shared/pages/auth/signin.js (loaded as part of the identity bundle)
  // also calls configureSdkBridge with `required: []` at module-top —
  // whichever module loads last wins, and on /login the editor's
  // signin.js wins, leaving governance optional + skipped (its
  // shouldActivate(claims) predicate returns false on empty claims).
  configureSdkBridge({ required: [PROJECT_GOVERNANCE] })
  try {
    const result = await sdkBridgeLoginAll({ email, password })
    if (!result.ok) {
      console.warn('[AppLoginCard] bridge login error:', result.error?.message)
    } else {
      const failed = result.results.filter(r => r.status === 'error')
      if (failed.length) {
        for (const r of failed) console.warn(`[AppLoginCard] bridge ${r.key} failed:`, r.error?.message)
      }
    }
  } catch (err) {
    console.warn('[AppLoginCard] bridge login threw:', err?.message || err)
  }
}

const _input = {
  tag: 'input',
  width: '100%',
  padding: 'A B',
  round: 'C',
  background: 'overlayStrong',
  border: 'none',
  outline: 'none',
  color: 'title',
  fontSize: 'A',
  boxSizing: 'border-box',
  // Visible focus ring for keyboard users — WCAG 2.4.7. The default
  // `outline: none` above strips the UA ring; restore it via box-shadow
  // on `:focus-visible` so mouse focus still gets a clean border-only
  // state.
  ':focus-visible': {
    boxShadow: '0 0 0 2px var(--color-blue)',
    outline: 'none'
  }
}

export const AppLoginCard = {
  flow: 'y',
  gap: 'C',
  padding: 'D',
  width: '100%',
  maxWidth: 'I',
  round: 'C',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  boxShadow: '0 24px 60px -24px var(--color-shadow)',

  eyebrow: 'Sign in',
  title: 'Welcome back',
  sub: 'Sign in to your workspace with your email and password.',
  redirectTo: '/',

  Head: {
    flow: 'y',
    gap: 'A',
    Eyebrow: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'blue',
      text: (el) => el.parent.parent.eyebrow || ''
    },
    Title: {
      // h1 — primary page heading. The login page is its own page, so a
      // single h1 here lands at the top of the document outline, which
      // every screen reader announces on landing.
      tag: 'h1',
      id: 'login-title',
      fontSize: 'D',
      fontWeight: '800',
      color: 'title',
      letterSpacing: '-0.02em',
      text: (el) => el.parent.parent.title || ''
    },
    Sub: {
      tag: 'p',
      id: 'login-sub',
      color: 'caption',
      fontSize: 'A',
      text: (el) => el.parent.parent.sub || ''
    }
  },

  Form: {
    tag: 'form',
    flow: 'y',
    gap: 'A',
    // The form's accessible name is the page title; its description is
    // the sub-line. Together they tell a screen reader user what the
    // form does + what credentials it expects.
    'aria-labelledby': 'login-title',
    'aria-describedby': 'login-sub login-error',
    onSubmit: async (ev, el, s) => {
      ev.preventDefault()
      const root = el.node
      const email = (root.querySelector('input[name="email"]')?.value || '').trim().toLowerCase()
      const password = root.querySelector('input[name="password"]')?.value || ''
      const errBox = root.querySelector('[data-lg-err]')
      if (errBox) errBox.textContent = ''
      if (!email || !password) {
        if (errBox) errBox.textContent = 'Email and password are required.'
        return
      }
      s.root.update({ _loginLoading: true })
      try {
        // Symbols SDK + sdk-bridge federation + token persistence (matches signin.js).
        // signInWithPassword (Supabase direct) was removed — single auth flow.
        const data = await el.sdk.login(email, password)
        const resolvedEmail = data?.user?.email || email
        // Await federation BEFORE the redirect — otherwise the page
        // navigates away mid-flight and the governance Supabase JWT
        // never gets persisted, leaving the workspace 401-ing on
        // every API call. _fanOutBridgeLogin swallows its own errors.
        await _fanOutBridgeLogin(resolvedEmail, password)
        await el.call('initializeUserSession', { loginData: data })
        s.root.update({
          signedIn: true,
          currentUserEmail: resolvedEmail,
          currentUserId: data?.user?.id || null
        })
        if (typeof window !== 'undefined') {
          const target = (el.parent && el.parent.redirectTo) || '/'
          window.location.replace(target)
        }
      } catch (e) {
        s.root.update({ _loginLoading: false })
        if (errBox) errBox.textContent = e.message || 'Sign-in failed.'
      }
    },

    // Email field — explicit labels + input. Use unique key names to
    // avoid clashing with any registered `Email` / `Password` /
    // `Label` brand components via smbls auto-extend.
    EmailLabel: {
      tag: 'label',
      attr: { for: 'login-email' },
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'caption',
      text: 'Email'
    },
    EmailInput: { ..._input, id: 'login-email', type: 'email', name: 'email', placeholder: 'name@symbols.app', required: 'true', autocomplete: 'email', 'aria-required': 'true', 'aria-describedby': 'login-error' },

    PasswordLabel: {
      tag: 'label',
      attr: { for: 'login-password' },
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'caption',
      marginTop: 'V',
      text: 'Password'
    },
    PasswordInput: { ..._input, id: 'login-password', type: 'password', name: 'password', placeholder: 'Password', required: 'true', autocomplete: 'current-password', 'aria-required': 'true', 'aria-describedby': 'login-error' },

    // Error span: aria-live=polite so screen readers announce the
    // message when it appears, role=status as a graceful fallback for
    // older AT. data-lg-err kept for the imperative writer above.
    Error: { tag: 'span', id: 'login-error', attr: { 'data-lg-err': 'true', role: 'status', 'aria-live': 'polite' }, fontSize: 'Z2', color: 'red', minHeight: 'A' },

    Submit: {
      tag: 'button',
      type: 'submit',
      width: '100%',
      padding: 'A B',
      round: 'C',
      background: 'blue',
      color: 'white',
      fontSize: 'A',
      fontWeight: '700',
      border: 'none',
      marginTop: 'V',
      cursor: (el, s) => (s.root?._loginLoading ? 'wait' : 'pointer'),
      opacity: (el, s) => (s.root?._loginLoading ? '0.7' : '1'),
      pointerEvents: (el, s) => (s.root?._loginLoading ? 'none' : 'auto'),
      disabled: (el, s) => (s.root?._loginLoading ? 'true' : null),
      text: (el, s) => (s.root?._loginLoading ? 'Signing in…' : 'Sign in'),
      ':hover': { background: 'blue.7' },
      ':focus-visible': {
        outline: 'none',
        boxShadow: '0 0 0 3px var(--color-blue), 0 0 0 5px var(--color-overlay)'
      }
    }
  },

  Footer: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'caption',
    align: 'center',
    text: 'No account? Ask an admin to create one for you.'
  }
}
